import { Breakpoint, useCurrentBreakpoint } from './media';

export type ColumnWidths<K extends string = string> = Record<K, string>;

export type ResponsiveColumnWidthsConfig<K extends string = string> = Partial<
  Record<Breakpoint, ColumnWidths<K>>
>;

const BREAKPOINT_ORDER: ReadonlyArray<Breakpoint> = [
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
];

/**
 * Возвращает ширину колонок для текущего брейкпоинта.
 *
 * Конфиг — словарь по брейкпоинтам, значения — объекты ширин колонок
 * (строки вида `'301px'`). Если для текущего брейкпоинта записи нет,
 * берётся ближайшая нижняя, затем — наименьшая из заданных как fallback.
 *
 * @typeParam K - Ключи колонок. Выводится из конфига, все записи должны иметь одинаковый набор ключей.
 * @param config - Конфиг ширин по брейкпоинтам.
 * @returns Объект ширин колонок для текущего брейкпоинта.
 */
export const useResponsiveColumnWidths = <K extends string>(
  config: ResponsiveColumnWidthsConfig<K>,
): ColumnWidths<K> => {
  const current = useCurrentBreakpoint();
  const currentIndex = BREAKPOINT_ORDER.indexOf(current);

  for (let i = currentIndex; i >= 0; i--) {
    const bp = BREAKPOINT_ORDER[i];
    if (config[bp]) {
      return config[bp] as ColumnWidths<K>;
    }
  }

  for (let i = 0; i < BREAKPOINT_ORDER.length; i++) {
    const bp = BREAKPOINT_ORDER[i];
    if (config[bp]) {
      return config[bp] as ColumnWidths<K>;
    }
  }

  return {} as ColumnWidths<K>;
};
